import { useMemo } from "react";
import { useSelector } from "react-redux";
import { Button, Spin, Table, Typography } from "antd";
import type { TableColumnsType } from "antd";
import { useUserPermissions } from "@components/PermissionedComponents";
import { buildVisualizerRows, cellDialogTitle, columnLabel, rowsWithNotes, visualizerPlayers } from "@mappers/notes.mapper.mjs";
import type { VisualizerColumn, VisualizerOptions } from "@mappers/notes.mapper.mjs";
import type { Note, NotesState } from "@store/reducers/notes.reducer";
import { formattedPlayerName } from "@utils/name_utils";
import type { NoteDialogRequest } from "../NoteDialog";
import { FittedTags } from "../FittedTags";
import { ColumnsEditor } from "./ColumnsEditor";
import { VisualizerNote } from "./VisualizerNote";

interface TeamPlayer {
    player_id: number;
    first_name: string;
    last_name: string;
    number?: number | string | null;
    position?: string | null;
}

interface VisualizerPlayer {
    id: number;
    name: string;
    detail: string;
}

interface VisualizerRow {
    player: VisualizerPlayer;
    cells: Note[][];
}

interface VisualizerProps {
    /** The notes that pass the filters, newest first. */
    notes: Note[];
    /** The player ids of the player filter, after `resolveNoteFilters`. */
    playerIds: number[];
    columns: VisualizerColumn[];
    onColumnsChange: (columns: VisualizerColumn[]) => void;
    presetName: string;
    onPresetNameChange: (name: string) => void;
    options: VisualizerOptions;
    onOptionsChange: (options: VisualizerOptions) => void;
    loading: boolean;
    onOpenDialog: (request: NoteDialogRequest) => void;
}

const PLAYER_COLUMN_WIDTH = 140;
const NOTE_COLUMN_MIN_WIDTH = 280;

const playerDetail = (player: TeamPlayer) => [player.number ? `#${player.number}` : null, player.position].filter(Boolean).join(" · ");

export const Visualizer = ({
    notes,
    playerIds,
    columns,
    onColumnsChange,
    presetName,
    onPresetNameChange,
    options,
    onOptionsChange,
    loading,
    onOpenDialog,
}: VisualizerProps) => {
    const teamPlayers: TeamPlayer[] = useSelector(({ team }: any) => team.players);
    const teamName: string = useSelector(({ team }: any) => team.info.display_name);
    const timeZone: string = useSelector(({ team }: any) => team.info.home_timezone);
    const myUuid: string | undefined = useSelector(({ auth }: any) => auth.currentUser.uuid);
    const attributes = useSelector((state: { notes: NotesState }) => state.notes.attributes);
    const canWrite = useUserPermissions()?.WriteNotes;

    const players = useMemo(
        () =>
            teamPlayers.map((p) => ({
                id: p.player_id,
                name: formattedPlayerName(teamName, `${p.first_name} ${p.last_name}`),
                detail: playerDetail(p),
            })),
        [teamPlayers, teamName],
    );

    const rows = useMemo(() => {
        const all = buildVisualizerRows(notes, visualizerPlayers(players, { playerIds }), columns);
        return options.hideEmpty ? rowsWithNotes(all) : all;
    }, [notes, players, playerIds, columns, options.hideEmpty]);

    const tableColumns: TableColumnsType<VisualizerRow> = [
        {
            key: "player",
            title: <div className="notes-viz-column-name">Player</div>,
            fixed: "left",
            width: PLAYER_COLUMN_WIDTH,
            className: "notes-viz-col",
            render: (_, { player }) => (
                <>
                    <div className="notes-viz-player-name">{player.name}</div>
                    <Typography.Text type="secondary">{player.detail}</Typography.Text>
                </>
            ),
        },
        ...columns.map((column, index) => ({
            key: `column-${index}`,
            title: (
                <div className="notes-viz-column-head">
                    <span className="notes-viz-column-name">{columnLabel(column)}</span>
                    {/* A header shows at most 3 tags. */}
                    <FittedTags tags={column.tags} max={3} className="notes-viz-column-tags" />
                </div>
            ),
            className: "notes-viz-col notes-viz-note-col",
            render: (_: unknown, { player, cells }: VisualizerRow) => {
                const cell = cells[index];
                const openAdd = () =>
                    onOpenDialog({
                        lockedPlayerId: player.id,
                        initialTags: column.tags,
                        columnTags: column.tags,
                        title: cellDialogTitle(player.name, column),
                    });
                return (
                    <div className="notes-viz-cell">
                        {cell.map((note) => (
                            <VisualizerNote
                                key={note.uuid}
                                note={note}
                                column={column}
                                attributes={attributes}
                                myUuid={myUuid}
                                timeZone={timeZone}
                                truncate={options.truncateNotes}
                                onEdit={(edited) => onOpenDialog({ note: edited, lockedPlayerId: player.id, columnTags: column.tags })}
                            />
                        ))}
                        {/* One add button for every cell. In an empty cell it floats, so the cell takes only the height of its row. */}
                        {canWrite && (
                            <Button
                                className={cell.length === 0 ? "notes-viz-cell-add notes-viz-cell-add-float" : "notes-viz-cell-add"}
                                block
                                size="small"
                                type="dashed"
                                data-attr="notes.visualizer.add-note"
                                onClick={openAdd}
                            >
                                + Add note
                            </Button>
                        )}
                    </div>
                );
            },
        })),
    ];

    return (
        <>
            <ColumnsEditor
                columns={columns}
                onChange={onColumnsChange}
                presetName={presetName}
                onPresetNameChange={onPresetNameChange}
                options={options}
                onOptionsChange={onOptionsChange}
            />
            <Spin spinning={loading}>
                <Table<VisualizerRow>
                    className="notes-viz-table"
                    size="small"
                    bordered
                    pagination={false}
                    rowKey={(row) => row.player.id}
                    columns={tableColumns}
                    dataSource={rows}
                    // A fixed layout wraps long notes. The note columns share the spare width, and the table scrolls when they reach their minimum.
                    tableLayout="fixed"
                    scroll={{ x: PLAYER_COLUMN_WIDTH + NOTE_COLUMN_MIN_WIDTH * columns.length }}
                    locale={{ emptyText: options.hideEmpty ? "No player has notes in these columns." : "No players to show." }}
                />
            </Spin>
        </>
    );
};
